/**
 * EmployeeBidirectionalSyncModal — DWUKIERUNKOWA synchronizacja PRACOWNIKÓW MY ↔ Symfonia FK,
 * model DWUFAZOWY z interaktywnym rozstrzyganiem konfliktów (decyzja Eugenii 2026-09-01).
 *
 * Odbicie `BidirectionalSyncModal` (kontrahenci) na encji pracownicy. Różnice statystyk wykrycia:
 * dochodzi `linked` (pracownicy nowo powiązani po kodzie/PESEL) i `unmatched` (bez pary FK).
 *
 * Etapy:
 *  1. **confirm** — wyjaśnienie: zmiany jednostronne stosuję automatycznie, przy rozbieżności
 *     zapytam o decyzję per pole.
 *  2. **detect** — polling jobu WYKRYCIA (`?direction=both`). Zmiany jednostronne zastosowane,
 *     konflikty wracają w `result.conflicts`. Brak konfliktów → ekran „gotowe". Są → etap resolve.
 *  3. **resolve** — dla każdego pola konfliktowego user wybiera: „Weź z Symfonii" (zapis u nas) /
 *     „Wyślij naszą" (push do FK) / „Ignoruj" (bez zmian, wróci następnym razem). Bulk-akcje na górze.
 *  4. **apply** — polling jobu ZASTOSOWANIA (`?direction=both&phase=apply`) z wyborami usera.
 *
 * Endpoint `POST /erp-integrations/:id/sync-employees?direction=both[&phase=apply]` — tylko FK
 * w trybie bezpośrednim (przycisk pokazujemy wyłącznie dla integracji `symfonia_fk`).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeftRight,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  Search,
  Square,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

import { isJobInFlight } from './job-in-flight';
import { type SyncRecordDetail, SyncResultPanel, type SyncResultTile } from './SyncResultPanel';
import type { JobDto } from './TestConnectionModal';

type Stage = 'confirm' | 'detect' | 'resolve' | 'apply';
type ConflictChoice = 'keep_fk' | 'keep_ours' | 'ignore';

interface Props {
  open: boolean;
  integrationId: string | null;
  integrationName: string;
  onClose: () => void;
}

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 3 * 60_000;
/** Powyżej tylu pracowników z konfliktem grupy startują zwinięte (lista zostaje krótka). */
const COLLAPSE_THRESHOLD = 6;

interface DetectStats {
  pushedToFk?: number;
  updatedLocal?: number;
  linked?: number;
  conflicts?: number;
  conflictedEmployees?: number;
  unchanged?: number;
  excluded?: number;
  unmatched?: number;
  skipped?: number;
}

interface ApplyStats {
  pushedToFk?: number;
  updatedLocal?: number;
  ignored?: number;
  skipped?: number;
}

interface FieldConflict {
  field: string;
  label: string;
  ours: string | null;
  fk: string | null;
}

interface EmployeeConflict {
  employeeId: string;
  employeeName: string;
  fkId: number;
  fields: FieldConflict[];
}

interface DetectResultPayload {
  outcome?: 'ok' | 'partial' | 'no_op';
  stats?: DetectStats;
  warnings?: string[];
  conflicts?: EmployeeConflict[];
  details?: Record<string, SyncRecordDetail[]>;
}

interface ApplyResultPayload {
  outcome?: 'ok' | 'partial' | 'no_op';
  stats?: ApplyStats;
  warnings?: string[];
}

/** Wybory usera: employeeId → (field → choice). */
type Resolutions = Record<string, Record<string, ConflictChoice>>;

const PREFIX = 'tenant.settings.integrations.employeeBidirectionalSync';

export function EmployeeBidirectionalSyncModal({
  open,
  integrationId,
  integrationName,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<Stage>('confirm');
  const [jobId, setJobId] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [conflicts, setConflicts] = useState<EmployeeConflict[]>([]);
  const [resolutions, setResolutions] = useState<Resolutions>({});
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState('');

  // Reset przy otwarciu/zamknięciu + timeout tickera per polling job.
  useEffect(() => {
    if (!open) {
      setStage('confirm');
      setJobId(null);
      setTimedOut(false);
      setConflicts([]);
      setResolutions({});
      setCollapsed({});
      setFilter('');
      return;
    }
    if ((stage !== 'detect' && stage !== 'apply') || !jobId) return;
    setTimedOut(false);
    const timer = window.setTimeout(() => setTimedOut(true), POLL_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [open, stage, jobId]);

  const startMutation = useMutation({
    mutationFn: async () => {
      if (!integrationId) throw new Error('No integrationId');
      const res = await api.post<{ jobId: string }>(
        `/erp-integrations/${integrationId}/sync-employees?direction=both`,
        {},
      );
      return res.data.jobId;
    },
    onSuccess: (id) => {
      setJobId(id);
      setStage('detect');
    },
  });

  const applyMutation = useMutation({
    mutationFn: async () => {
      if (!integrationId) throw new Error('No integrationId');
      const payload = {
        resolutions: conflicts.map((c) => ({
          employeeId: c.employeeId,
          fields: c.fields.map((f) => ({
            field: f.field,
            choice: resolutions[c.employeeId]?.[f.field] ?? 'keep_fk',
          })),
        })),
      };
      const res = await api.post<{ jobId: string }>(
        `/erp-integrations/${integrationId}/sync-employees?direction=both&phase=apply`,
        payload,
      );
      return res.data.jobId;
    },
    onSuccess: (id) => {
      setJobId(id);
      setStage('apply');
    },
  });

  const jobQ = useQuery({
    queryKey: ['erp-sync-job', jobId],
    queryFn: async () => {
      const res = await api.get<{ job: JobDto }>(`/erp-sync-jobs/${jobId}`);
      return res.data.job;
    },
    enabled: !!jobId && open && !timedOut && (stage === 'detect' || stage === 'apply'),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return POLL_INTERVAL_MS;
    },
  });

  const job = jobQ.data ?? null;

  // Detect ukończony → przejdź do rozstrzygania (gdy są konflikty) i zasiej domyślne wybory.
  useEffect(() => {
    if (stage !== 'detect' || job?.status !== 'completed') return;
    const result = (job.result ?? null) as DetectResultPayload | null;
    const found = result?.conflicts ?? [];
    if (found.length > 0) {
      setConflicts(found);
      setResolutions((prev) => {
        if (Object.keys(prev).length > 0) return prev; // nie nadpisuj wyborów usera
        const seeded: Resolutions = {};
        for (const c of found) {
          seeded[c.employeeId] = {};
          for (const f of c.fields) seeded[c.employeeId]![f.field] = 'keep_fk';
        }
        return seeded;
      });
      // Przy wielu pracownikach domyślnie zwiń grupy — lista zostaje krótka i skanowalna.
      if (found.length > COLLAPSE_THRESHOLD) {
        const seededCollapsed: Record<string, boolean> = {};
        for (const c of found) seededCollapsed[c.employeeId] = true;
        setCollapsed(seededCollapsed);
      }
      setStage('resolve');
    }
    void queryClient.invalidateQueries({ queryKey: ['erp-integrations'] });
  }, [stage, job?.status, job?.result, queryClient]);

  useEffect(() => {
    if (stage === 'apply' && (job?.status === 'completed' || job?.status === 'failed')) {
      void queryClient.invalidateQueries({ queryKey: ['erp-integrations'] });
    }
  }, [stage, job?.status, queryClient]);

  const setChoice = (employeeId: string, field: string, choice: ConflictChoice) => {
    setResolutions((prev) => ({
      ...prev,
      [employeeId]: { ...prev[employeeId], [field]: choice },
    }));
  };

  const setAll = (choice: ConflictChoice) => {
    setResolutions(() => {
      const next: Resolutions = {};
      for (const c of conflicts) {
        next[c.employeeId] = {};
        for (const f of c.fields) next[c.employeeId]![f.field] = choice;
      }
      return next;
    });
  };

  // Bulk dla JEDNEGO pracownika (nie ruszamy pozostałych).
  const setEmployeeAll = (employeeId: string, choice: ConflictChoice) => {
    setResolutions((prev) => {
      const c = conflicts.find((x) => x.employeeId === employeeId);
      if (!c) return prev;
      const forC: Record<string, ConflictChoice> = {};
      for (const f of c.fields) forC[f.field] = choice;
      return { ...prev, [employeeId]: forC };
    });
  };

  const toggleCollapse = (employeeId: string) =>
    setCollapsed((prev) => ({ ...prev, [employeeId]: !prev[employeeId] }));

  const totalConflictFields = useMemo(
    () => conflicts.reduce((sum, c) => sum + c.fields.length, 0),
    [conflicts],
  );

  const filteredConflicts = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return conflicts;
    return conflicts.filter((c) => c.employeeName.toLowerCase().includes(q));
  }, [conflicts, filter]);

  // Wybory różne od domyślnego („Weź z Symfonii") = niezapisana praca na etapie rozstrzygania.
  const resolutionsDirty = useMemo(
    () =>
      conflicts.some((c) =>
        c.fields.some((f) => (resolutions[c.employeeId]?.[f.field] ?? 'keep_fk') !== 'keep_fk'),
      ),
    [conflicts, resolutions],
  );

  if (!open) return null;

  const detectResult = (job?.result ?? null) as DetectResultPayload | null;
  const applyResult = (job?.result ?? null) as ApplyResultPayload | null;

  const ds = detectResult?.stats ?? {};
  const dd = detectResult?.details ?? {};
  const detectTiles: SyncResultTile[] = [
    {
      key: 'sent',
      label: t(`${PREFIX}.stats.pushedToFk`),
      count: ds.pushedToFk ?? 0,
      tone: 'updated',
      records: dd.sent ?? [],
    },
    {
      key: 'pulled',
      label: t(`${PREFIX}.stats.updatedLocal`),
      count: ds.updatedLocal ?? 0,
      tone: 'added',
      records: dd.pulled ?? [],
    },
    {
      key: 'linked',
      label: t(`${PREFIX}.stats.linked`),
      count: ds.linked ?? 0,
      tone: 'muted',
      records: dd.linked ?? [],
    },
    {
      key: 'conflicts',
      label: t(`${PREFIX}.stats.conflicts`),
      count: ds.conflictedEmployees ?? 0,
      tone: 'warning',
    },
    {
      key: 'unchanged',
      label: t(`${PREFIX}.stats.unchanged`),
      count: ds.unchanged ?? 0,
      tone: 'muted',
      records: dd.unchanged ?? [],
    },
    {
      key: 'excluded',
      label: t(`${PREFIX}.stats.excluded`),
      count: ds.excluded ?? 0,
      tone: 'muted',
      records: dd.excluded ?? [],
    },
    {
      key: 'unmatched',
      label: t(`${PREFIX}.stats.unmatched`),
      count: ds.unmatched ?? 0,
      tone: 'muted',
      records: dd.unmatched ?? [],
    },
    {
      key: 'skipped',
      label: t(`${PREFIX}.stats.skipped`),
      count: ds.skipped ?? 0,
      tone: 'danger',
      records: dd.skipped ?? [],
    },
  ];

  // Wysyłanie żądania albo zadanie w toku — tło i Esc nie zamykają okna (X i „Zamknij" działają).
  const running =
    startMutation.isPending ||
    applyMutation.isPending ||
    ((stage === 'detect' || stage === 'apply') && isJobInFlight(job, timedOut, jobQ.isError));

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <ArrowLeftRight className="h-4 w-4 text-accent-500" aria-hidden="true" />
          {t(`${PREFIX}.title`, { name: integrationName })}
        </span>
      }
      size={stage === 'resolve' ? 'xl' : 'lg'}
      expandable
      testId="employee-bidirectional-sync-modal"
      closeOnBackdrop={!running}
      closeOnEsc={!running}
      guardUnsaved
      dirty={stage === 'resolve' && resolutionsDirty}
      footer={
        <>
          {stage === 'confirm' && (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => startMutation.mutate()}
                disabled={startMutation.isPending}
                data-testid="employee-bidirectional-sync-start-btn"
              >
                {startMutation.isPending ? t(`${PREFIX}.starting`) : t(`${PREFIX}.startButton`)}
              </Button>
            </>
          )}
          {stage === 'resolve' && (
            <>
              <Button variant="secondary" onClick={onClose}>
                {t(`${PREFIX}.resolve.backButton`)}
              </Button>
              <Button
                variant="primary"
                onClick={() => applyMutation.mutate()}
                disabled={applyMutation.isPending || totalConflictFields === 0}
                data-testid="employee-bidirectional-apply-btn"
              >
                {applyMutation.isPending
                  ? t(`${PREFIX}.resolve.applying`)
                  : t(`${PREFIX}.resolve.applyButton`)}
              </Button>
            </>
          )}
          {(stage === 'detect' || stage === 'apply') && (
            <Button
              variant="primary"
              onClick={onClose}
              data-testid="employee-bidirectional-sync-close-btn"
            >
              {t('common.close')}
            </Button>
          )}
        </>
      }
    >
      {stage === 'confirm' && (
        <div className="space-y-4" data-testid="employee-bidirectional-sync-stage-confirm">
          <p className="text-sm text-text-secondary">{t(`${PREFIX}.confirmHint`)}</p>
          <div className="rounded-md border border-accent-500/30 bg-accent-500/5 p-3 text-sm text-text-secondary">
            {t(`${PREFIX}.conflictNote`)}
          </div>
          {startMutation.isError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-400">
              {t(`${PREFIX}.startError`)}
            </div>
          )}
        </div>
      )}

      {stage === 'detect' && (
        <div
          className="space-y-4"
          data-testid={`employee-bidirectional-sync-status-${
            job?.status === 'completed' || job?.status === 'failed'
              ? job.status
              : timedOut
                ? 'timeout'
                : (job?.status ?? 'running')
          }`}
        >
          {!timedOut && (!job || job.status === 'pending' || job.status === 'running') && (
            <RunningRow title={t(`${PREFIX}.running`)} hint={t(`${PREFIX}.runningHint`)} />
          )}

          {timedOut && job?.status !== 'completed' && job?.status !== 'failed' && <TimeoutRow />}

          {job?.status === 'completed' && (
            <div className="space-y-3">
              {/* Detect bez konfliktów (z konfliktami → efekt przenosi na etap resolve). */}
              <div className="flex items-start gap-3 rounded-md border border-green-500/30 bg-green-500/10 p-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" aria-hidden="true" />
                <div>
                  <div className="font-medium text-green-700 dark:text-green-400">
                    {t(`${PREFIX}.noConflictsTitle`)}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {t(`${PREFIX}.noConflictsHint`)}
                  </div>
                </div>
              </div>
              <SyncResultPanel
                tiles={detectTiles}
                onNavigate={onClose}
                linkBase="/employees"
                testId="emp-bidir-result"
                listHeightClass="h-72"
              />
            </div>
          )}

          {job?.status === 'failed' && <FailedBlock result={job.result} />}
        </div>
      )}

      {stage === 'resolve' && (
        // Etap na pełną szerokość ciała okna: pasek narzędzi przykleja się do góry przewijanego
        // ciała, a własne odstępy listy zastępują odstępy okna (-mx/-my).
        <div
          className="-mx-5 -my-4 flex flex-col"
          data-testid="employee-bidirectional-sync-stage-resolve"
        >
          {/* Pasek narzędzi — przyklejony u góry przy scrollu długiej listy. */}
          <div className="sticky top-0 z-10 space-y-3 border-b border-border-primary bg-bg-elevated p-4">
            <div>
              <div className="text-sm font-medium text-text-primary">
                {t(`${PREFIX}.resolve.title`)}
              </div>
              <p className="mt-1 text-xs text-text-secondary">
                {t(`${PREFIX}.resolve.summary`, {
                  fields: totalConflictFields,
                  employees: conflicts.length,
                })}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Szukajka po nazwisku — sensowna dopiero przy wielu pracownikach. */}
              {conflicts.length > COLLAPSE_THRESHOLD && (
                <Input
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder={t(`${PREFIX}.resolve.searchPlaceholder`)}
                  aria-label={t(`${PREFIX}.resolve.searchPlaceholder`)}
                  data-testid="employee-conflict-search"
                  prefixIcon={<Search className="h-4 w-4" aria-hidden="true" />}
                  containerClassName="min-w-[12rem] flex-1"
                />
              )}
              <span className="text-xs text-text-tertiary">{t(`${PREFIX}.resolve.bulkLabel`)}</span>
              <Button variant="secondary" size="sm" onClick={() => setAll('keep_fk')}>
                {t(`${PREFIX}.resolve.bulkFk`)}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setAll('keep_ours')}>
                {t(`${PREFIX}.resolve.bulkOurs`)}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setAll('ignore')}>
                {t(`${PREFIX}.resolve.bulkIgnore`)}
              </Button>
            </div>
          </div>

          {/* Lista grup — jeden pracownik = zwijalna grupa z bulkiem per osoba. */}
          <div className="space-y-2 p-4">
            {filteredConflicts.length === 0 && (
              <p className="py-6 text-center text-sm text-text-tertiary">
                {t(`${PREFIX}.resolve.noMatch`)}
              </p>
            )}
            {filteredConflicts.map((c) => {
              const isCollapsed = collapsed[c.employeeId] ?? false;
              return (
                <div
                  key={c.employeeId}
                  className="overflow-hidden rounded-md border border-border-primary"
                  data-testid={`conflict-employee-${c.employeeId}`}
                >
                  {/* Nagłówek grupy: rozwiń/zwiń + nazwisko + licznik. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleCollapse(c.employeeId)}
                    className="w-full justify-start gap-2 rounded-none bg-bg-secondary !px-3 !py-2 hover:bg-bg-tertiary"
                    aria-expanded={!isCollapsed}
                    data-testid={`conflict-group-toggle-${c.employeeId}`}
                    leftIcon={
                      isCollapsed ? (
                        <ChevronRight className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                      )
                    }
                  >
                    <span
                      className="truncate text-sm font-medium text-text-primary"
                      title={c.employeeName}
                    >
                      {c.employeeName}
                    </span>
                    <span className="ml-auto shrink-0 rounded-full bg-yellow-500/15 px-2 py-0.5 text-[0.68rem] font-medium text-yellow-700 dark:text-yellow-400">
                      {t(`${PREFIX}.resolve.fieldsBadge`, { count: c.fields.length })}
                    </span>
                  </Button>

                  {!isCollapsed && (
                    <div className="border-t border-border-primary">
                      {/* Szybki wybór dla CAŁEGO pracownika — tylko w rozwiniętej grupie. */}
                      <div className="flex flex-wrap items-center gap-1.5 border-b border-border-primary px-3 py-1.5">
                        <span className="mr-1 text-[0.68rem] text-text-tertiary">
                          {t(`${PREFIX}.resolve.bulkLabel`)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEmployeeAll(c.employeeId, 'keep_fk')}
                        >
                          {t(`${PREFIX}.resolve.bulkFk`)}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEmployeeAll(c.employeeId, 'keep_ours')}
                        >
                          {t(`${PREFIX}.resolve.bulkOurs`)}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEmployeeAll(c.employeeId, 'ignore')}
                        >
                          {t(`${PREFIX}.resolve.bulkIgnore`)}
                        </Button>
                      </div>
                      <ul className="divide-y divide-border-primary">
                        {c.fields.map((f) => {
                          const choice = resolutions[c.employeeId]?.[f.field] ?? 'keep_fk';
                          return (
                            <li
                              key={f.field}
                              className="grid grid-cols-1 items-stretch gap-2 px-3 py-2 sm:grid-cols-[6rem_1fr_1fr_8rem]"
                            >
                              <span className="self-center text-xs font-medium text-text-secondary">
                                {f.label}
                              </span>
                              {/* Klik w wartość = wybór tej strony. Zielony = wybrana. */}
                              <ChoiceValue
                                topLabel={t(`${PREFIX}.resolve.columnOurs`)}
                                value={f.ours}
                                emptyLabel={t(`${PREFIX}.resolve.emptyValue`)}
                                selected={choice === 'keep_ours'}
                                onSelect={() => setChoice(c.employeeId, f.field, 'keep_ours')}
                                testId={`conflict-choice-${c.employeeId}-${f.field}-ours`}
                              />
                              <ChoiceValue
                                topLabel={t(`${PREFIX}.resolve.columnFk`)}
                                value={f.fk}
                                emptyLabel={t(`${PREFIX}.resolve.emptyValue`)}
                                selected={choice === 'keep_fk'}
                                onSelect={() => setChoice(c.employeeId, f.field, 'keep_fk')}
                                testId={`conflict-choice-${c.employeeId}-${f.field}-fk`}
                              />
                              <ChoiceIgnore
                                label={t(`${PREFIX}.resolve.choice.ignore`)}
                                selected={choice === 'ignore'}
                                onSelect={() => setChoice(c.employeeId, f.field, 'ignore')}
                                testId={`conflict-choice-${c.employeeId}-${f.field}-ignore`}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {applyMutation.isError && (
            <div className="mx-4 mb-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-400">
              {t(`${PREFIX}.resolve.applyError`)}
            </div>
          )}

          {/* Notka o „Anuluj" — żeby było jasne, że sporne pola zostają na później. */}
          <p className="px-4 pb-4 text-xs text-text-tertiary">
            {t(`${PREFIX}.resolve.cancelNote`)}
          </p>
        </div>
      )}

      {stage === 'apply' && (
        <div
          className="space-y-4"
          data-testid={`employee-bidirectional-apply-status-${
            job?.status === 'completed' || job?.status === 'failed'
              ? job.status
              : timedOut
                ? 'timeout'
                : (job?.status ?? 'running')
          }`}
        >
          {!timedOut && (!job || job.status === 'pending' || job.status === 'running') && (
            <RunningRow
              title={t(`${PREFIX}.apply.running`)}
              hint={t(`${PREFIX}.apply.runningHint`)}
            />
          )}

          {timedOut && job?.status !== 'completed' && job?.status !== 'failed' && <TimeoutRow />}

          {job?.status === 'completed' && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-md border border-green-500/30 bg-green-500/10 p-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" aria-hidden="true" />
                <div>
                  <div className="font-medium text-green-700 dark:text-green-400">
                    {t(`${PREFIX}.apply.successTitle`)}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {t(`${PREFIX}.apply.successHint`)}
                  </div>
                </div>
              </div>
              <ApplyStatsGrid stats={applyResult?.stats ?? {}} />
              <WarningsBlock warnings={applyResult?.warnings ?? []} />
            </div>
          )}

          {job?.status === 'failed' && <FailedBlock result={job.result} />}
        </div>
      )}
    </Modal>
  );
}

function RunningRow({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="flex items-center gap-3 rounded-md bg-bg-secondary p-3">
      <Loader2 className="h-5 w-5 animate-spin text-accent-500" aria-hidden="true" />
      <div>
        <div className="font-medium text-text-primary">{title}</div>
        <div className="text-xs text-text-tertiary">{hint}</div>
      </div>
    </div>
  );
}

function TimeoutRow() {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
      <Clock className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" aria-hidden="true" />
      <div>
        <div className="font-medium text-red-600 dark:text-red-400">
          {t(`${PREFIX}.timeoutTitle`)}
        </div>
        <div className="text-xs text-text-secondary">{t(`${PREFIX}.timeoutHint`)}</div>
      </div>
    </div>
  );
}

function FailedBlock({ result }: { result: Record<string, unknown> | null }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/10 p-3">
        <AlertCircle className="mt-0.5 h-5 w-5 text-red-600 dark:text-red-400" aria-hidden="true" />
        <div>
          <div className="font-medium text-red-600 dark:text-red-400">
            {t(`${PREFIX}.failedTitle`)}
          </div>
          <div className="text-xs text-text-secondary">{t(`${PREFIX}.failedHint`)}</div>
        </div>
      </div>
      {result && (
        <pre className="max-h-64 overflow-auto rounded-md border border-red-500/30 bg-red-500/5 p-3 font-mono text-xs text-red-700 dark:text-red-400">
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}

function WarningsBlock({ warnings }: { warnings: string[] }) {
  const { t } = useTranslation();
  if (warnings.length === 0) return null;
  return (
    <details className="rounded-md border border-yellow-500/30 bg-yellow-500/5 p-3">
      <summary className="cursor-pointer text-xs font-medium text-yellow-700 dark:text-yellow-400">
        {t(`${PREFIX}.warningsLabel`, { count: warnings.length })}
      </summary>
      <ul className="mt-2 space-y-1 text-xs text-text-secondary">
        {warnings.slice(0, 30).map((w, i) => (
          <li key={i} className="font-mono">
            • {w}
          </li>
        ))}
      </ul>
    </details>
  );
}

/** Karta wyboru wartości (U nas / W Symfonii): klik = wybór, zielone = wybrana. */
function ChoiceValue({
  topLabel,
  value,
  emptyLabel,
  selected,
  onSelect,
  testId,
}: {
  topLabel: string;
  value: string | null;
  emptyLabel: string;
  selected: boolean;
  onSelect: () => void;
  testId: string;
}) {
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={onSelect}
      aria-pressed={selected}
      data-testid={testId}
      className={`h-auto w-full items-start justify-start gap-2 py-1.5 text-left ${
        selected
          ? 'border-green-500 bg-green-500/10 ring-1 ring-green-500'
          : 'hover:border-accent-500/50'
      }`}
      leftIcon={
        selected ? (
          <CheckSquare className="h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
        ) : (
          <Square className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
        )
      }
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[0.62rem] uppercase tracking-wide text-text-tertiary">
          {topLabel}
        </span>
        <span
          className={`block truncate text-sm font-normal ${
            value
              ? selected
                ? 'text-green-800 dark:text-green-300'
                : 'text-text-primary'
              : 'italic text-text-tertiary'
          }`}
          title={value ?? undefined}
        >
          {value || emptyLabel}
        </span>
      </span>
    </Button>
  );
}

/** Karta „Ignoruj" — trzecia, wzajemnie wykluczająca się opcja. */
function ChoiceIgnore({
  label,
  selected,
  onSelect,
  testId,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
  testId: string;
}) {
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={onSelect}
      aria-pressed={selected}
      data-testid={testId}
      className={`w-full justify-start gap-2 ${
        selected
          ? 'border-green-500 bg-green-500/10 text-green-800 ring-1 ring-green-500 dark:text-green-300'
          : 'hover:border-accent-500/50'
      }`}
      leftIcon={
        selected ? (
          <CheckSquare className="h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
        ) : (
          <Square className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
        )
      }
    >
      {label}
    </Button>
  );
}

function ApplyStatsGrid({ stats }: { stats: ApplyStats }) {
  const { t } = useTranslation();
  const cells: Array<[string, number | undefined, string]> = [
    [t(`${PREFIX}.apply.stats.pushedToFk`), stats.pushedToFk, 'text-accent-500'],
    [t(`${PREFIX}.apply.stats.updatedLocal`), stats.updatedLocal, 'text-green-600'],
    [t(`${PREFIX}.apply.stats.ignored`), stats.ignored, 'text-text-tertiary'],
    [t(`${PREFIX}.apply.stats.skipped`), stats.skipped, 'text-red-600'],
  ];
  return <StatsGrid cells={cells} />;
}

function StatsGrid({ cells }: { cells: Array<[string, number | undefined, string]> }) {
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      {cells.map(([label, val, color]) => (
        <div
          key={label}
          className="rounded-md border border-border-primary bg-bg-secondary p-2 text-center"
        >
          <div className={`text-xl font-bold ${color}`}>{val ?? 0}</div>
          <div className="text-xs text-text-tertiary">{label}</div>
        </div>
      ))}
    </div>
  );
}
